import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, Plus, Download, Upload, Package, ClipboardList, Truck, Users, ArrowRight } from "lucide-react";
import { adminApi } from "../../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "catalogue", label: "Catalogue", icon: Package },
  { key: "balances", label: "Balances", icon: ClipboardList },
  { key: "requests", label: "Requests", icon: ClipboardList },
  { key: "pos", label: "Purchase Orders", icon: Truck },
  { key: "suppliers", label: "Suppliers", icon: Users },
];

export default function AdminStock() {
  const [tab, setTab] = useState("catalogue");
  return (
    <div data-testid="admin-stock-page">
      <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Stock &amp; Purchasing</h1>
      <div className="mt-3 inline-flex flex-wrap bg-white border border-[#cfd7db] rounded-md p-1" data-testid="stock-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`stock-tab-${t.key}`}
                  className={`px-3 py-1.5 text-xs font-display uppercase tracking-widest rounded inline-flex items-center gap-1.5 ${tab === t.key ? "bg-[#f28a16] text-white" : "text-[#3f4b52]"}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>
      <div className="mt-4">
        {tab === "catalogue" && <Catalogue />}
        {tab === "balances" && <Balances />}
        {tab === "requests" && <Requests />}
        {tab === "pos" && <PurchaseOrders />}
        {tab === "suppliers" && <Suppliers />}
      </div>
    </div>
  );
}

const Catalogue = () => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [create, setCreate] = useState(null);
  const load = async () => { try { setRows((await axios.get(`${API}/admin/stock/items?q=${encodeURIComponent(q)}`)).data); } catch { toast.error("Load failed"); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  const save = async (r) => {
    try {
      if (r.id) await axios.put(`${API}/admin/stock/items/${r.id}`, r);
      else await axios.post(`${API}/admin/stock/items`, r);
      toast.success("Saved"); setCreate(null); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU, part, barcode, vehicle…"
               data-testid="catalogue-search" className="flex-1 min-w-[220px] h-10 border rounded px-3 text-sm" />
        <button
          onClick={() => adminApi.openDoc(`${API}/admin/stock/items/export.csv`, { action: "download", filename: "stock-catalogue.csv" })}
          type="button"
          data-testid="catalogue-export"
          className="inline-flex items-center gap-1 px-3 h-10 border rounded text-xs font-display uppercase tracking-widest"><Download size={12} /> CSV export</button>
        <button onClick={() => setCreate({ category: "glass", active: true, unit: "ea" })} data-testid="catalogue-new"
                className="inline-flex items-center gap-1 px-3 h-10 bg-[#f28a16] text-white rounded text-xs font-display uppercase tracking-widest"><Plus size={12} /> New item</button>
      </div>
      {!rows ? <Loader2 className="animate-spin text-[#f28a16]" /> : (
        <div className="bg-white border border-[#cfd7db] rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f5f6f6] text-xs font-display uppercase tracking-widest text-[#3f4b52]"><tr>
              <th className="text-left px-3 py-2">SKU</th><th className="text-left px-3 py-2">Part</th><th className="text-left px-3 py-2">Category</th><th className="text-left px-3 py-2">Description</th><th className="text-right px-3 py-2">Cost ex</th>
            </tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} onClick={() => setCreate(r)} data-testid={`catalogue-row-${r.id}`} className="border-t hover:bg-[#f5f6f6] cursor-pointer">
                <td className="px-3 py-2 font-mono text-xs">{r.sku}</td><td className="px-3 py-2 text-xs">{r.part_code || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.category}</td><td className="px-3 py-2 text-xs">{r.description}</td>
                <td className="px-3 py-2 text-xs text-right">{r.cost_ex_vat != null ? `R ${r.cost_ex_vat.toFixed(2)}` : "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-[#3f4b52]" data-testid="catalogue-empty">No items.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {create && <ItemModal row={create} onSave={save} onClose={() => setCreate(null)} />}
    </div>
  );
};

const ItemModal = ({ row, onSave, onClose }) => {
  const [r, setR] = useState(row);
  const set = (k, v) => setR((s) => ({ ...s, [k]: v }));
  return (
    <Modal onClose={onClose} testId="item-modal">
      <div className="font-display uppercase tracking-widest text-sm">{r.id ? "Edit item" : "New item"}</div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="SKU *"><input value={r.sku || ""} onChange={(e) => set("sku", e.target.value)} data-testid="item-sku" className="w-full h-10 border rounded px-2 text-sm" /></Field>
        <Field label="Part code"><input value={r.part_code || ""} onChange={(e) => set("part_code", e.target.value)} className="w-full h-10 border rounded px-2 text-sm" /></Field>
        <Field label="Category *"><select value={r.category} onChange={(e) => set("category", e.target.value)} className="w-full h-10 border rounded px-2 text-sm">
          {["glass", "mould", "clips", "sensor", "adhesive", "consumable", "other"].map((c) => <option key={c}>{c}</option>)}
        </select></Field>
        <Field label="Barcode"><input value={r.barcode || ""} onChange={(e) => set("barcode", e.target.value)} className="w-full h-10 border rounded px-2 text-sm" /></Field>
        <div className="col-span-2"><Field label="Description *"><input value={r.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="item-desc" className="w-full h-10 border rounded px-2 text-sm" /></Field></div>
        <Field label="Cost ex VAT"><input type="number" step="0.01" value={r.cost_ex_vat ?? ""} onChange={(e) => set("cost_ex_vat", e.target.value ? Number(e.target.value) : null)} className="w-full h-10 border rounded px-2 text-sm" /></Field>
        <Field label="Reorder min"><input type="number" step="0.01" value={r.reorder_min ?? ""} onChange={(e) => set("reorder_min", e.target.value ? Number(e.target.value) : null)} className="w-full h-10 border rounded px-2 text-sm" /></Field>
      </div>
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 border rounded py-2 text-sm">Cancel</button>
        <button onClick={() => onSave(r)} data-testid="item-save" className="flex-1 bg-[#f28a16] text-white rounded py-2 text-sm font-display uppercase tracking-widest">Save</button>
      </div>
    </Modal>
  );
};

const Balances = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { (async () => { try { setRows((await axios.get(`${API}/admin/stock/balances`)).data); } catch { toast.error("Load failed"); } })(); }, []);
  if (!rows) return <Loader2 className="animate-spin text-[#f28a16]" />;
  return (
    <div className="bg-white border border-[#cfd7db] rounded-md overflow-x-auto" data-testid="stock-balances">
      <table className="w-full text-sm">
        <thead className="bg-[#f5f6f6] text-xs font-display uppercase tracking-widest text-[#3f4b52]"><tr>
          <th className="text-left px-3 py-2">SKU</th><th className="text-left px-3 py-2">Location</th>
          <th className="text-right px-3 py-2">On hand</th><th className="text-right px-3 py-2">Reserved</th>
          <th className="text-right px-3 py-2">Allocated</th><th className="text-right px-3 py-2">Available</th>
        </tr></thead>
        <tbody>
          {rows.map((b) => (
            <tr key={`${b.stock_item_id}-${b.location_id}`} className="border-t">
              <td className="px-3 py-2 font-mono text-xs">{b.sku}</td>
              <td className="px-3 py-2 text-xs">{b.location_name} <span className="text-xs text-[#3f4b52]">({b.location_kind})</span></td>
              <td className="px-3 py-2 text-xs text-right">{b.on_hand}</td>
              <td className="px-3 py-2 text-xs text-right">{b.reserved}</td>
              <td className="px-3 py-2 text-xs text-right">{b.allocated}</td>
              <td className={`px-3 py-2 text-xs text-right font-semibold ${b.available <= 0 ? "text-red-600" : "text-emerald-700"}`}>{b.available}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-[#3f4b52]" data-testid="balances-empty">No stock recorded yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

const Requests = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = async () => { try { setRows((await axios.get(`${API}/admin/stock-requests`)).data); } catch { toast.error("Load failed"); } };
  useEffect(() => { load(); }, []);
  const approve = async (id, lines) => {
    setBusy(id);
    try {
      await axios.post(`${API}/admin/stock-requests/${id}/approve`, { lines: lines.map((L) => ({ line_id: L.id, qty_approved: L.qty_requested })) });
      toast.success("Approved"); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(null); }
  };
  if (!rows) return <Loader2 className="animate-spin text-[#f28a16]" />;
  return (
    <div className="space-y-2" data-testid="stock-requests-list">
      {rows.length === 0 && <div className="bg-white border border-dashed border-[#cfd7db] rounded p-8 text-center text-[#3f4b52]" data-testid="requests-empty">No requests.</div>}
      {rows.map((r) => (
        <div key={r.id} className="bg-white border border-[#cfd7db] rounded p-3" data-testid={`req-row-${r.id}`}>
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">{r.status} · {new Date(r.created_at).toLocaleString("en-ZA")}</div>
              <div className="font-display font-bold">{r.created_by_email} · {r.lines.length} line(s)</div>
              <ul className="mt-1 text-xs text-[#3f4b52]">{r.lines.map((L) => <li key={L.id}>• {L.qty_requested} × {L.description || L.part_code || L.stock_item_id} ({L.urgency})</li>)}</ul>
            </div>
            {r.status === "requested" && (
              <button onClick={() => approve(r.id, r.lines)} disabled={busy === r.id} data-testid={`req-approve-${r.id}`}
                      className="bg-[#f28a16] text-white rounded px-3 py-1.5 text-xs font-display uppercase tracking-widest">
                {busy === r.id ? "…" : "Approve"} <ArrowRight size={12} className="inline" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const PurchaseOrders = () => {
  const [rows, setRows] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = async () => {
    try {
      const [p, s, i] = await Promise.all([axios.get(`${API}/admin/purchase-orders`), axios.get(`${API}/admin/suppliers`), axios.get(`${API}/admin/stock/items`)]);
      setRows(p.data); setSuppliers(s.data); setItems(i.data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);
  if (!rows) return <Loader2 className="animate-spin text-[#f28a16]" />;
  return (
    <div className="space-y-3" data-testid="po-list">
      <div className="flex justify-end">
        <button onClick={() => setCreating(true)} data-testid="po-new" className="bg-[#f28a16] text-white rounded px-3 py-2 text-xs font-display uppercase tracking-widest"><Plus size={12} className="inline" /> New PO</button>
      </div>
      <div className="bg-white border border-[#cfd7db] rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#f5f6f6] text-xs font-display uppercase tracking-widest text-[#3f4b52]"><tr>
            <th className="text-left px-3 py-2">PO #</th><th className="text-left px-3 py-2">Supplier</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Expected</th><th className="text-right px-3 py-2">Total inc</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-t" data-testid={`po-row-${p.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{p.po_number}</td>
                <td className="px-3 py-2 text-xs">{p.supplier_name}</td>
                <td className="px-3 py-2 text-xs uppercase">{p.status}</td>
                <td className="px-3 py-2 text-xs">{p.expected_date || "—"}</td>
                <td className="px-3 py-2 text-xs text-right">R {p.total_inc_vat.toFixed(2)}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => adminApi.openDoc(`${API}/admin/purchase-orders/${p.id}/pdf.pdf`)}
                    type="button"
                    data-testid={`po-pdf-${p.id}`}
                    className="text-xs text-[#f28a16] hover:underline">PDF</button>
                  {" · "}
                  <button
                    onClick={() => adminApi.openDoc(`${API}/admin/purchase-orders/${p.id}/export.csv`, { action: "download", filename: `po-${p.po_number}.csv` })}
                    type="button"
                    data-testid={`po-csv-${p.id}`}
                    className="text-xs text-[#f28a16] hover:underline">CSV</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-[#3f4b52]" data-testid="po-empty">No purchase orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {creating && <NewPOModal suppliers={suppliers} items={items} onClose={() => { setCreating(false); load(); }} />}
    </div>
  );
};

const NewPOModal = ({ suppliers, items, onClose }) => {
  const [supplier_id, setSupplier] = useState(suppliers[0]?.id || "");
  const [lines, setLines] = useState([{ stock_item_id: "", qty: 1, unit_cost_ex_vat: 0, vat_pct: 15 }]);
  const [expected, setExpected] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!supplier_id) return toast.error("Choose supplier");
    if (lines.some((L) => !L.stock_item_id || L.qty <= 0)) return toast.error("Complete each line");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/purchase-orders`, { supplier_id, lines, expected_date: expected || null });
      toast.success("PO created"); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  const setLine = (i, k, v) => setLines((L) => L.map((x, j) => (i === j ? { ...x, [k]: v } : x)));
  return (
    <Modal onClose={onClose} testId="po-new-modal">
      <div className="font-display uppercase tracking-widest text-sm">New Purchase Order</div>
      <Field label="Supplier"><select value={supplier_id} onChange={(e) => setSupplier(e.target.value)} data-testid="po-supplier" className="w-full h-10 border rounded px-2 text-sm">
        <option value="">Choose…</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select></Field>
      <Field label="Expected date"><input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} data-testid="po-expected" className="w-full h-10 border rounded px-2 text-sm" /></Field>
      {lines.map((L, i) => (
        <div key={i} className="grid grid-cols-4 gap-1 items-end">
          <select value={L.stock_item_id} onChange={(e) => setLine(i, "stock_item_id", e.target.value)} data-testid={`po-line-item-${i}`} className="col-span-2 h-9 border rounded px-2 text-xs">
            <option value="">Item…</option>{items.map((it) => <option key={it.id} value={it.id}>{it.sku} — {it.description}</option>)}
          </select>
          <input type="number" min="1" value={L.qty} onChange={(e) => setLine(i, "qty", Number(e.target.value))} data-testid={`po-line-qty-${i}`} className="h-9 border rounded px-2 text-xs" />
          <input type="number" step="0.01" value={L.unit_cost_ex_vat} onChange={(e) => setLine(i, "unit_cost_ex_vat", Number(e.target.value))} data-testid={`po-line-cost-${i}`} placeholder="Cost ex" className="h-9 border rounded px-2 text-xs" />
        </div>
      ))}
      <button onClick={() => setLines([...lines, { stock_item_id: "", qty: 1, unit_cost_ex_vat: 0, vat_pct: 15 }])} className="text-xs text-[#f28a16]" data-testid="po-add-line">+ Add line</button>
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 border rounded py-2 text-sm">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="po-submit" className="flex-1 bg-[#f28a16] text-white rounded py-2 text-sm font-display uppercase tracking-widest">Create</button>
      </div>
    </Modal>
  );
};

const Suppliers = () => {
  const [rows, setRows] = useState(null);
  const [create, setCreate] = useState(null);
  const load = async () => { try { setRows((await axios.get(`${API}/admin/suppliers`)).data); } catch { toast.error("Load failed"); } };
  useEffect(() => { load(); }, []);
  const save = async (r) => {
    try {
      if (r.id) await axios.put(`${API}/admin/suppliers/${r.id}`, r);
      else await axios.post(`${API}/admin/suppliers`, r);
      toast.success("Saved"); setCreate(null); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  if (!rows) return <Loader2 className="animate-spin text-[#f28a16]" />;
  return (
    <div className="space-y-2" data-testid="suppliers-list">
      <div className="flex justify-end">
        <button onClick={() => setCreate({ name: "", active: true, preferred_categories: [] })} data-testid="supplier-new"
                className="bg-[#f28a16] text-white rounded px-3 py-2 text-xs font-display uppercase tracking-widest"><Plus size={12} className="inline" /> New supplier</button>
      </div>
      {rows.map((s) => (
        <div key={s.id} onClick={() => setCreate(s)} data-testid={`supplier-row-${s.id}`}
             className="bg-white border border-[#cfd7db] rounded p-3 cursor-pointer hover:border-[#f28a16]">
          <div className="font-display font-bold text-[#111619]">{s.name}</div>
          <div className="text-xs text-[#3f4b52]">{s.phone || "—"} · {s.email || "—"} · terms: {s.payment_terms || "—"}</div>
        </div>
      ))}
      {rows.length === 0 && <div className="text-[#3f4b52] py-6 text-center" data-testid="suppliers-empty">No suppliers yet.</div>}
      {create && (
        <Modal onClose={() => setCreate(null)} testId="supplier-modal">
          <div className="font-display uppercase tracking-widest text-sm">{create.id ? "Edit supplier" : "New supplier"}</div>
          <Field label="Name *"><input value={create.name} onChange={(e) => setCreate({ ...create, name: e.target.value })} data-testid="supplier-name" className="w-full h-10 border rounded px-2 text-sm" /></Field>
          <Field label="Contact"><input value={create.contact_name || ""} onChange={(e) => setCreate({ ...create, contact_name: e.target.value })} className="w-full h-10 border rounded px-2 text-sm" /></Field>
          <Field label="Phone"><input value={create.phone || ""} onChange={(e) => setCreate({ ...create, phone: e.target.value })} className="w-full h-10 border rounded px-2 text-sm" /></Field>
          <Field label="Email"><input value={create.email || ""} onChange={(e) => setCreate({ ...create, email: e.target.value })} className="w-full h-10 border rounded px-2 text-sm" /></Field>
          <Field label="Payment terms"><input value={create.payment_terms || ""} onChange={(e) => setCreate({ ...create, payment_terms: e.target.value })} className="w-full h-10 border rounded px-2 text-sm" /></Field>
          <div className="flex gap-2">
            <button onClick={() => setCreate(null)} className="flex-1 border rounded py-2 text-sm">Cancel</button>
            <button onClick={() => save(create)} data-testid="supplier-save" className="flex-1 bg-[#f28a16] text-white rounded py-2 text-sm font-display uppercase tracking-widest">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">{label}</span>{children}
  </label>
);
const Modal = ({ children, onClose, testId }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md p-5 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto" data-testid={testId}>{children}</div>
  </div>
);
